import React from "react";
import classNames from "classnames";
import Link from "next/link";
import { usePathname, useRouter, useSearchParams } from "next/navigation";

import { Action } from "@/constants/events";
import { useEventTracker } from "@/hooks/useEventTracker";
import useGetSearchParamValues from "@/hooks/useGetSearchParamValues";
import toggleURLSearchParamValue from "@/utils/toggleURLSearchParamValue";

import Switch from "@/components/botanic/Switch/Switch";
import IconSvg from "@/components/IconSvg";
import { useIcons } from "@/components/MenuFilters/hooks";
import { MenuItemFilterSection } from "@/components/MenuFilters/types/MenuFilters";
import TrackImpression from "@/components/TrackImpression";

import { FilterQueryStringKeyProp, NoFollowLinkProp } from "./DynamicFilters";
import { ToggleFilterType } from "./types";

const ToggleFilter: React.FC<{
  filter: ToggleFilterType;
  sectionPadding?: string;
  noFollowLink?: NoFollowLinkProp;
  includeFilterEvents?: boolean;
  eventCategory?: string;
  filterQueryStringKey?: FilterQueryStringKeyProp;
}> = ({
  filter,
  sectionPadding,
  noFollowLink,
  includeFilterEvents,
  eventCategory,
  filterQueryStringKey = (filterName) => filterName,
}) => {
  const {
    name,
    label,
    sublabel,
    filterIcon,
    labelIcon,
    count = 0,
    showCount = false,
  } = filter;
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const targetURLSearchParams = new URLSearchParams(searchParams?.toString());
  // * Remove pagination params when clicking fulfillment filter link.
  targetURLSearchParams.delete("page");

  const { push } = useRouter();
  const { publishEvent } = useEventTracker();
  const queryStringKey = filterQueryStringKey(name);
  const getSearchParamValues = useGetSearchParamValues();
  const [appliedValue] = getSearchParamValues(queryStringKey);

  const { getIconPath } = useIcons();
  const disabled = count < 1;

  const labelIconClassnames = classNames("self-center", {
    "text-verified": name === MenuItemFilterSection.BrandVerified,
  });

  if (disabled) {
    return null;
  }

  const href = {
    pathname,
    query: toggleURLSearchParamValue(
      targetURLSearchParams,
      queryStringKey,
      "true",
    ).toString(),
  };

  return (
    <TrackImpression
      trackingFunction={() => {
        if (includeFilterEvents && eventCategory) {
          publishEvent({
            action: Action.impression,
            category: eventCategory,
            label: filter.label,
          });
        }
      }}
    >
      <div
        className={classNames(
          "flex flex-col border-t border-b border-light-grey py-[17px] md:gap-sm",
          {
            "md:pb-lg": sublabel,
            "p-lg": sectionPadding,
          },
        )}
      >
        <Link
          href={href}
          rel={noFollowLink?.(href) ? "nofollow" : undefined}
          className="flex justify-between"
          data-testid="toggle-filter"
        >
          <span className={classNames("flex font-bold")}>
            {filterIcon && (
              <IconSvg
                height="24"
                width="24"
                className="shrink-0 inline mr-xs"
                svgClassName="fill-[var(--color-default)]"
                filePath={getIconPath(
                  name as MenuItemFilterSection,
                  filterIcon,
                )}
                testId={`${name}-filter-icon`}
              />
            )}
            {label}&nbsp;
            {labelIcon && (
              <IconSvg
                height="16"
                width="16"
                className={labelIconClassnames}
                filePath={getIconPath(name as MenuItemFilterSection, labelIcon)}
              />
            )}
            {showCount && (
              <span className="text-grey text-start leading-none self-center">
                ({count.toLocaleString("en-us")})
              </span>
            )}
          </span>
          <Switch
            checked={appliedValue === "true"}
            checkedBgColor="bg-default"
            disabled={disabled}
            screenReaderText={label}
            onChange={() => {
              if (
                appliedValue !== "true" &&
                eventCategory &&
                includeFilterEvents
              ) {
                publishEvent({
                  action: Action.click,
                  category: eventCategory,
                  label: filter.label,
                });
              }
              push(`${href.pathname}?${href.query}`);
            }}
          />
        </Link>
        {sublabel && (
          <div className="flex items-center">
            <span className="text-xs font-normal whitespace-pre-wrap text-left text-grey">
              {sublabel}
            </span>
          </div>
        )}
      </div>
    </TrackImpression>
  );
};

export default ToggleFilter;
